import { FC } from "react";
import { useTranslation } from "react-i18next";
import Divider from "@mui/material/Divider";
import { HeaderMenu } from "../utils/styled";
import { authController } from "@reedexpo/authorization-component-ui";
import { Routes } from "routes";
import { useNavigate } from "react-router-dom";
import { makeStyles } from '@mui/styles';
import colors from "../../../common/styles/colors";
import { useGetAccess } from "../../../common/hooks/useGetAccess/useGetAccess";
import { OdinAccess } from "../../../common/enums";
import { LanguageSwitch } from "./components/languageSwitch/LanguageSwitch";
import { Nullable } from "../../../common/types";
import { AppHeaderOptions } from "../types";

type Props = {
  menuId: string;
  isMenuOpen: boolean;
  handleMenuClose: () => void;
  anchorEl: Nullable<HTMLElement>;
  options: AppHeaderOptions;
};

const useStyles = makeStyles({
  menuItem: {
    marginTop: 5,
    marginBottom: 5,
    padding: 12,
    paddingLeft: 10,
    justifyContent: "flex-start",
    width: "100%",
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: colors.lightGrey2
    }
  },
  lastItem: {
    marginTop: 5,
    marginBottom: 5
  },
  menuItemText: {
    fontSize: 14,
    color: colors.primary
  },
  disabled: {
    opacity: 0.25,
    pointerEvents: 'none'
  }
});

const AppHeaderMenu: FC<Props> = ({ menuId, isMenuOpen, handleMenuClose, anchorEl, options }) => {
  const { t } = useTranslation("common");
  const router = useNavigate();
  const { hasAccess: printerSetupAvailable } = useGetAccess(OdinAccess.PRINTER_SETUP);
  const classes = useStyles();

  const onLogoutClick = () => {
    const config = window["authSettings"];
    authController.initialize(config);
    authController.logout(Routes.homepage);
  };

  const onPrintSettingsClick = () => {
    router(Routes.printSettings);
    handleMenuClose();
  };

  const onGlobalSettingsClick = () => {
    router(Routes.globalSettings);
    handleMenuClose();
  }

  return (
    <HeaderMenu data-testid="header-menu" id={menuId} open={isMenuOpen} anchorEl={anchorEl} onClose={handleMenuClose}>
      <LanguageSwitch />

      {!options.hidePrintSettings && (
        <div
          className={`${classes.menuItem} ${!printerSetupAvailable && classes.disabled}`}
          data-testid="print-settings-item"
          onClick={onPrintSettingsClick}
        >
          <span className={classes.menuItemText}>{t("header.printerSettings")}</span>
        </div>
      )}

      <Divider />

      {!options.hideGlobalSettings && (
        <div className={classes.menuItem} data-testid="global-settings-item" onClick={onGlobalSettingsClick}>
          <span className={classes.menuItemText}>{t("header.globalSettings")}</span>
        </div>
      )}

      {!options.hideLogout && (
        <div>
          <Divider />

          <div className={`${classes.menuItem} ${classes.lastItem}`} data-testid="logout-menu-item" onClick={onLogoutClick}>
            <span className={classes.menuItemText}>{t("header.logOutLabel")}</span>
          </div>
        </div>
      )}
    </HeaderMenu>
  );
};

export default AppHeaderMenu;
